import { checkHql, type HqlCatalog, sameHql } from '@rock/hql';
import type { AppliesToDraft, AppliesToOwner, ConsiderationType, LessonType, TaskType, Timestamp } from '@rock/types';
import { appliesToOwnerCopy, hasWhenToApplyText } from '@rock/types';
import { considerationCadence } from '@rock/util';

/**
 * Why a consideration or lesson type's applies-to query isn't simply doing its job. Validity is derived
 * here, against the live catalog, and never stored: a catalog edit can break a query without touching it.
 */
export const QUERY_FLAGS = ['missing', 'invalid', 'unpublished', 'mixed', 'issue', 'review'] as const;
export type QueryFlag = (typeof QUERY_FLAGS)[number];

export const QUERY_FLAG_LABELS: Record<QueryFlag, string> = {
    missing: 'Not applied: no approved query',
    invalid: 'Not applied: invalid query',
    unpublished: 'Not applied: space unpublished',
    mixed: 'Not applied: mixed task cadence',
    issue: "Can't express",
    review: 'Awaiting review',
};

/** The red flags: a type with one of these reaches no home at all. The others wait on a human. */
export const BLOCKING_QUERY_FLAGS: readonly QueryFlag[] = ['missing', 'invalid', 'unpublished', 'mixed'];

export function isQueryFlag(value: string | null): value is QueryFlag {
    return (QUERY_FLAGS as readonly (string | null)[]).includes(value);
}

/** `taskTypes` are the type's own; drafts and retired ones are filtered out when judging cadence. */
export function queryFlags(type: ConsiderationType, taskTypes: TaskType[], catalog: HqlCatalog): QueryFlag[] {
    return flagsInOrder(type, catalog, { mixed: considerationCadence(taskTypes) === 'mixed' });
}

/** A lesson's flags: a consideration's, less the task cadence a lesson doesn't have. */
export function lessonQueryFlags(type: LessonType, catalog: HqlCatalog): QueryFlag[] {
    return flagsInOrder(type, catalog, { mixed: false });
}

/** The flags in `QUERY_FLAGS` order, which is how the badges render. */
function flagsInOrder(type: AppliesToOwner, catalog: HqlCatalog, opts: { mixed: boolean }): QueryFlag[] {
    const flags: QueryFlag[] = [];
    if (!type.appliesTo) {
        flags.push('missing');
    } else if (checkHql(type.appliesTo, catalog, { spaceId: type.spaceId }).errors.length > 0) {
        flags.push('invalid');
    }
    // No home can complete an unpublished space, so a query waiting on it waits forever.
    if (type.spaceId && !catalog.spaceIds.includes(type.spaceId)) flags.push('unpublished');
    if (opts.mixed) flags.push('mixed');
    if (type.appliesToDraft?.issue) flags.push('issue');
    else if (type.appliesToDraft) flags.push('review');
    return flags;
}

/**
 * How many approved types carry each flag. Only approved types count: a Notion draft is expected to have
 * no query yet, and the list these counts link to shows approved types by default.
 */
export function countQueryFlags(
    types: Pick<ConsiderationType, 'id' | 'documentStatus'>[],
    flagsById: Record<string, QueryFlag[]>,
): Record<QueryFlag, number> {
    const counts = Object.fromEntries(QUERY_FLAGS.map((flag) => [flag, 0])) as Record<QueryFlag, number>;
    for (const type of types) {
        if (type.documentStatus !== 'approved') continue;
        for (const flag of flagsById[type.id] ?? []) counts[flag]++;
    }
    return counts;
}

/**
 * An approval as the admin made it: `seen` is the draft that was waiting when they began (the one they
 * approved, edited, or were answering), and `fromDraft` says they started from it.
 */
export type Approval = { query: string; fromDraft: boolean; seen: AppliesToDraft | undefined };

/**
 * Whether an approval settles the waiting draft, so approving clears it: the draft asks the same thing,
 * or it's the one the admin began from (approved as drafted or edited) or answered (it offered no
 * query). A draft that arrived after they began is newer than anything they saw, so it stays for review.
 */
export function approvalSettlesDraft(type: AppliesToOwner, approval: Approval, catalog: HqlCatalog): boolean {
    const draft = type.appliesToDraft;
    if (!draft) return false;
    if (draft.query && sameHql(draft.query, approval.query, catalog, { spaceId: type.spaceId })) return true;
    return draft.draftedAt === approval.seen?.draftedAt && (approval.fromDraft || !draft.query);
}

/**
 * Types with neither an approved query nor a draft waiting, that the drafting step would draft: what
 * "Draft missing queries" asks for. One with no When To Apply text needs its query written by hand.
 */
export function typesMissingQueries(types: ConsiderationType[]): ConsiderationType[] {
    return types.filter((type) => !type.appliesTo && !type.appliesToDraft && !redraftBlocker(type));
}

/** How long a requested draft counts as on its way when nothing has written the type since. */
export const DRAFT_REQUEST_TIMEOUT_MS = 3 * 60_000;

/**
 * Whether a requested draft is still on its way. `requestWrote` is the `updatedAt` the request wrote, and
 * drafting always writes the type (even when it keeps no draft), so while the type still carries it
 * nothing has landed. A failed draft writes nothing, so the request gives up after
 * `DRAFT_REQUEST_TIMEOUT_MS`, counted on the browser's clock that stamped it.
 */
export function isDraftPending(
    type: Pick<AppliesToOwner, 'updatedAt'>,
    requestWrote: Timestamp | undefined,
    now: number,
): boolean {
    if (!requestWrote || now - Date.parse(requestWrote) > DRAFT_REQUEST_TIMEOUT_MS) return false;
    return type.updatedAt === requestWrote;
}

/** Why the drafting step would skip this type, or undefined when a redraft request will run. */
export function redraftBlocker(type: AppliesToOwner): string | undefined {
    const { textLabel } = appliesToOwnerCopy(type);
    if (!hasWhenToApplyText(type)) return `Add ${textLabel} text in Notion first.`;
    return undefined;
}
